import { useEffect } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { recordListeningProgress } from '../lib/listeningHistory';

const SAVE_INTERVAL_MS = 10000;
const MAX_SEEK_ATTEMPTS = 24;

const ListeningHistoryBridge = () => {
  const {
    currentTrack,
    isPlaying,
    activeSource,
    audioRef,
    videoRef,
    ytPlayerRef,
    playContent,
  } = useAudio();

  useEffect(() => {
    if (!isPlaying || currentTrack?.type === 'radio') return undefined;

    const save = () => {
      let position = 0;
      let duration = Number(currentTrack?.duration) || 0;

      if (activeSource === 'youtube' && ytPlayerRef?.current) {
        try {
          position = Number(ytPlayerRef.current.getCurrentTime?.()) || 0;
          duration = Number(ytPlayerRef.current.getDuration?.()) || duration;
        } catch {
          // Playback history is best-effort and must never interrupt playback.
        }
      } else if (audioRef?.current) {
        position = Number(audioRef.current.currentTime) || 0;
        duration = Number(audioRef.current.duration) || duration;
      }

      recordListeningProgress(currentTrack, { position, duration });
    };

    save();
    const interval = window.setInterval(save, SAVE_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      save();
    };
  }, [activeSource, audioRef, currentTrack, isPlaying, ytPlayerRef]);

  useEffect(() => {
    const resume = async (event) => {
      const item = event.detail;
      if (!item?.id) return;

      const resumePosition = Math.max(0, Number(item.position) || 0);

      const resumeTrack = {
        ...item,
        track_id: item.source_id || item.id,
        id: item.source_id || item.id,
        type: item.track_type,
        track_type: item.track_type,
        artist: item.artist,
        dj: item.artist,
        cover: item.cover,
        youtube_id: item.youtube_id || (item.track_type === 'youtube' ? item.source_id : ''),
      };
      const started = playContent(resumeTrack);
      if (!started) return;

      if (item.track_type === 'youtube') {

        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          try {
            if (ytPlayerRef.current?.seekTo) {
              ytPlayerRef.current.seekTo(resumePosition, true);
              window.clearInterval(timer);
            } else if (attempts >= MAX_SEEK_ATTEMPTS) {
              window.clearInterval(timer);
            }
          } catch {
            if (attempts >= MAX_SEEK_ATTEMPTS) window.clearInterval(timer);
          }
        }, 250);
        return;
      }

      const seek = () => {
        const media = item.track_type === 'video' ? videoRef.current : audioRef.current;
        if (!media || !Number.isFinite(media.duration) || media.duration <= 0) return false;
        media.currentTime = Math.min(resumePosition, Math.max(0, media.duration - 1));
        return true;
      };

      if (seek()) return;

      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (seek() || attempts >= MAX_SEEK_ATTEMPTS) window.clearInterval(timer);
      }, 250);
    };

    window.addEventListener('rastyle:resume-listening', resume);
    return () => window.removeEventListener('rastyle:resume-listening', resume);
  }, [audioRef, playContent, videoRef, ytPlayerRef]);

  return null;
};

export { MAX_SEEK_ATTEMPTS, SAVE_INTERVAL_MS };
export default ListeningHistoryBridge;
